import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, Button, Alert, CircularProgress,
  ToggleButtonGroup, ToggleButton, Stack, Divider, InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon, Download as DownloadIcon, Refresh as RefreshIcon,
  Person as PersonIcon, AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { api } from '../../utils/api';

// Audit & Compliance Center (#audit-log-page, Day 50).
// One admin surface that replaces routine pgAdmin/CloudWatch trips: five
// activity views + per-user DSAR dossier + AI investigation, each exportable.
// Backed by GET/POST /admin/audit/* (my-travel-agent-audit Lambda).
// Visual idiom intentionally matches SystemManagementTab (teal #2D6E6A,
// MUI tables, ToggleButtonGroup) — consistency is correct for an admin tool.

const TEAL = '#2D6E6A';

type ViewKey = 'internal_actions' | 'system_events' | 'moderation' | 'consents' | 'deletions';

const VIEWS: { key: ViewKey; label: string }[] = [
  { key: 'internal_actions', label: 'Internal Actions' },
  { key: 'system_events', label: 'System Events' },
  { key: 'moderation', label: 'Safety & Moderation' },
  { key: 'consents', label: 'Consents' },
  { key: 'deletions', label: 'Deletions & Retention' },
];

// Column definitions per view: [header, row-accessor]. Accessors are defensive
// (optional chaining) so a missing field renders blank, never throws.
const COLUMNS: Record<ViewKey, { header: string; get: (r: any) => React.ReactNode }[]> = {
  internal_actions: [
    { header: 'When', get: (r) => fmtDate(r.performed_at) },
    { header: 'Admin', get: (r) => r.admin_name || '—' },
    { header: 'Action', get: (r) => <Chip size="small" label={r.action_type} variant="outlined" /> },
    { header: 'Target', get: (r) => r.target_name || '—' },
    { header: 'Reason', get: (r) => r.reason || '—' },
  ],
  system_events: [
    { header: 'When', get: (r) => fmtDate(r.created_at) },
    { header: 'Actor', get: (r) => r.actor_name || r.actor_type || '—' },
    { header: 'Action', get: (r) => <Chip size="small" label={r.action} variant="outlined" /> },
    { header: 'Resource', get: (r) => r.resource || '—' },
    { header: 'IP', get: (r) => r.ip_address || '—' },
  ],
  moderation: [
    { header: 'When', get: (r) => fmtDate(r.created_at) },
    { header: 'Type', get: (r) => r.content_type || '—' },
    { header: 'Reason', get: (r) => <Chip size="small" label={r.reason_code} color={r.reason_code === 'csam' || r.reason_code === 'illegal' ? 'error' : 'default'} variant="outlined" /> },
    { header: 'Status', get: (r) => r.status || '—' },
    { header: 'Hold', get: (r) => (r.preservation_hold ? <Chip size="small" label="HOLD" color="error" /> : '—') },
    { header: 'NCMEC', get: (r) => (r.ncmec_report_id ? 'Reported' : '—') },
    { header: 'Reporter', get: (r) => r.reporter_name || '—' },
    { header: 'Actions', get: (r) => r.action_count ?? 0 },
  ],
  consents: [
    { header: 'When', get: (r) => fmtDate(r.accepted_at) },
    { header: 'User', get: (r) => r.user_name || '—' },
    { header: 'Context', get: (r) => r.consent_context || '—' },
    { header: 'Age confirmed', get: (r) => (r.age_confirmed ? 'Yes' : 'No') },
    { header: 'Terms', get: (r) => r.terms_version || '—' },
    { header: 'Privacy', get: (r) => r.privacy_version || '—' },
    { header: 'IP', get: (r) => r.ip_address || '—' },
  ],
  deletions: [
    { header: 'When', get: (r) => fmtDate(r.created_at) },
    { header: 'Event', get: (r) => <Chip size="small" label={r.event_type} variant="outlined" /> },
    { header: 'User (email if resolvable)', get: (r) => r.user_email || r.user_id || '—' },
  ],
};

function fmtDate(s?: string): string {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? String(s) : d.toLocaleString();
}

// Flatten a row to a CSV-safe record using the active view's columns. We export
// the underlying values (not the React nodes), pulling raw fields by header.
function toCsv(view: ViewKey, rows: any[]): string {
  const fieldMap: Record<ViewKey, [string, string][]> = {
    internal_actions: [['When', 'performed_at'], ['Admin', 'admin_name'], ['Admin email', 'admin_email'], ['Action', 'action_type'], ['Target', 'target_name'], ['Target email', 'target_email'], ['Reason', 'reason']],
    system_events: [['When', 'created_at'], ['Actor', 'actor_name'], ['Actor type', 'actor_type'], ['Action', 'action'], ['Resource', 'resource'], ['IP', 'ip_address']],
    moderation: [['When', 'created_at'], ['Type', 'content_type'], ['Reason', 'reason_code'], ['Status', 'status'], ['Preservation hold', 'preservation_hold'], ['NCMEC report id', 'ncmec_report_id'], ['Reporter', 'reporter_name'], ['Action count', 'action_count']],
    consents: [['When', 'accepted_at'], ['User', 'user_name'], ['Email', 'user_email'], ['Context', 'consent_context'], ['Age confirmed', 'age_confirmed'], ['Terms', 'terms_version'], ['Privacy', 'privacy_version'], ['AUP', 'aup_version'], ['IP', 'ip_address']],
    deletions: [['When', 'created_at'], ['Event', 'event_type'], ['User id', 'user_id'], ['Email', 'user_email']],
  };
  const cols = fieldMap[view];
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.map(([h]) => h).join(',');
  const lines = rows.map((r) => cols.map(([, f]) => esc(r[f])).join(','));
  return [header, ...lines].join('\n');
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const AuditComplianceTab: React.FC = () => {
  const [view, setView] = useState<ViewKey>('internal_actions');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // DSAR
  const [dsarUserId, setDsarUserId] = useState('');
  const [dsar, setDsar] = useState<any>(null);
  const [dsarLoading, setDsarLoading] = useState(false);
  const [dsarError, setDsarError] = useState('');

  // AI investigation
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ view, limit: '100' });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (q.trim()) params.set('q', q.trim());
      const res = await api.get(`/admin/audit/activity?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setRows(data.rows || []);
        setTotal(data.total ?? (data.rows?.length || 0));
      } else {
        setError(data.error || 'Failed to load audit data');
        setRows([]);
      }
    } catch {
      setError('Network error loading audit data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [view, from, to, q]);

  const runDsar = async () => {
    const id = dsarUserId.trim();
    if (!id) { setDsarError('Enter a user ID (UUID)'); return; }
    setDsarLoading(true);
    setDsarError('');
    setDsar(null);
    try {
      const res = await api.get(`/admin/audit/dsar?userId=${encodeURIComponent(id)}`);
      const data = await res.json();
      if (data.success) {
        setDsar(data.dsar);
      } else {
        setDsarError(data.error || 'DSAR lookup failed');
      }
    } catch {
      setDsarError('Network error during DSAR lookup');
    } finally {
      setDsarLoading(false);
    }
  };

  const runInvestigate = async () => {
    if (!question.trim()) { setAiError('Enter a question'); return; }
    setAiLoading(true);
    setAiError('');
    setAnswer('');
    try {
      const scope: any = {};
      if (from) scope.from = from;
      if (to) scope.to = to;
      const res = await api.post('/admin/audit/investigate', { question: question.trim(), scope });
      const data = await res.json();
      if (data.success) {
        if (data.aiDisabled) {
          setAiError('AI investigation is turned off (feature flag). Records remain available in the views above.');
        } else if (data.redirectedOffTopic) {
          setAiError('That question was outside the audit scope. Try asking about admin actions, reports, consents, or deletions.');
        } else {
          setAnswer(data.answer || '(no answer returned)');
        }
      } else {
        setAiError(data.error || 'Investigation failed');
      }
    } catch {
      setAiError('Network error during investigation');
    } finally {
      setAiLoading(false);
    }
  };

  const cols = COLUMNS[view];

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Audit &amp; Compliance
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review and export the platform audit trail — internal actions, system events, safety &amp; moderation,
        consents, and deletions — without going to the database. Generate a per-user dossier or ask a question
        about a time range.
      </Typography>

      {/* View selector */}
      <ToggleButtonGroup
        value={view}
        exclusive
        size="small"
        onChange={(_, v) => v && setView(v)}
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        {VIEWS.map((v) => (
          <ToggleButton key={v.key} value={v.key} sx={{ textTransform: 'none' }}>
            {v.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
          value={from} onChange={(e) => setFrom(e.target.value)}
        />
        <TextField
          size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
          value={to} onChange={(e) => setTo(e.target.value)}
        />
        <TextField
          size="small" placeholder="Search…" value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchActivity(); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 200 }}
        />
        <Button
          variant="contained" startIcon={<RefreshIcon />} onClick={fetchActivity}
          sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: '#1F4E4A' } }}
        >
          Load
        </Button>
        <Button
          variant="outlined" startIcon={<DownloadIcon />}
          disabled={rows.length === 0}
          onClick={() => downloadCsv(`audit-${view}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(view, rows))}
          sx={{ textTransform: 'none', color: TEAL, borderColor: TEAL }}
        >
          Export CSV
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Activity table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: TEAL }} /></Box>
      ) : rows.length === 0 ? (
        <Alert severity="info">No records for this view and filter. Adjust the date range or click Load.</Alert>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">{total} record(s){total > rows.length ? ` (showing first ${rows.length})` : ''}</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {cols.map((c) => <TableCell key={c.header} sx={{ fontWeight: 600 }}>{c.header}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} hover>
                    {cols.map((c) => <TableCell key={c.header}>{c.get(r)}</TableCell>)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Divider sx={{ my: 3 }} />

      {/* DSAR dossier */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        <PersonIcon sx={{ verticalAlign: 'middle', mr: 1, color: TEAL }} fontSize="small" />
        Per-user dossier (DSAR)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Assemble every record about one user — consents, actions taken on them, reports they filed, and deletion status.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small" placeholder="User ID (UUID)" value={dsarUserId}
          onChange={(e) => setDsarUserId(e.target.value)} sx={{ minWidth: 320 }}
        />
        <Button
          variant="contained" onClick={runDsar} disabled={dsarLoading}
          sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: '#1F4E4A' } }}
        >
          {dsarLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate dossier'}
        </Button>
        {dsar && (
          <Button
            variant="outlined" startIcon={<DownloadIcon />}
            onClick={() => downloadCsv(`dsar-${dsarUserId}-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(dsar, null, 2))}
            sx={{ textTransform: 'none', color: TEAL, borderColor: TEAL }}
          >
            Export JSON
          </Button>
        )}
      </Stack>
      {dsarError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDsarError('')}>{dsarError}</Alert>}
      {dsar && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {dsar.subject?.name} · {dsar.subject?.email} · {dsar.subject?.role}
          </Typography>
          <Typography variant="caption" color="text.secondary">Generated {fmtDate(dsar.generatedAt)}</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`${dsar.consents?.length || 0} consents`} variant="outlined" />
            <Chip label={`${dsar.adminActionsTakenOnUser?.length || 0} actions on user`} variant="outlined" />
            <Chip label={`${dsar.reportsFiledByUser?.length || 0} reports filed`} variant="outlined" />
            <Chip label={`${dsar.deletionRetentionEvents?.length || 0} deletion events`} variant="outlined" />
          </Stack>
        </Paper>
      )}

      <Divider sx={{ my: 3 }} />

      {/* AI investigation */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        <AIIcon sx={{ verticalAlign: 'middle', mr: 1, color: TEAL }} fontSize="small" />
        Ask the audit trail
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Ask a question about the current date range (e.g. “list every plan change in June and who made it”, or
        “any deletions missing a reason”). The answer is advisory — the records above are the source of truth.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small" fullWidth placeholder="Ask a question about the audit trail…"
          value={question} onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runInvestigate(); }}
        />
        <Button
          variant="contained" startIcon={<AIIcon />} onClick={runInvestigate} disabled={aiLoading}
          sx={{ bgcolor: TEAL, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#1F4E4A' } }}
        >
          {aiLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Ask'}
        </Button>
      </Stack>
      {aiError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAiError('')}>{aiError}</Alert>}
      {answer && (
        <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap', bgcolor: '#FAFAF8' }}>
          <Typography variant="body2">{answer}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AuditComplianceTab;
